import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { adminLogin, isLoggedIn, isAdmin } from '../utils/authService';

const AdminLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  // Check if already logged in
  useEffect(() => {
    if (isLoggedIn() && isAdmin()) {
      navigate('/admin-portal');
    }
  }, [navigate]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await adminLogin(email, password);
      navigate('/admin-portal');
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <LoginPageContainer>
      <Header />
      <LoginContent>
        <LoginCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <LoginHeader>
            <h2>Admin Login</h2>
            <p>Sign in to access the admin portal</p>
          </LoginHeader>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <LoginForm onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Email</Label>
              <Input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Password</Label>
              <Input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </FormGroup>
            
            <LoginButton 
              type="submit"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </LoginButton>
          </LoginForm>
          
          <LoginFooter>
            <p>Admin Access:</p>
            <CredentialsInfo>
              <div>Use the admin email and password you created in Supabase.</div>
            </CredentialsInfo>
            <Note>Note: Make sure you've set up the admin user in Supabase with the 'admin' role in metadata.</Note>
          </LoginFooter>
        </LoginCard>
      </LoginContent>
      <Footer />
    </LoginPageContainer>
  );
};

// Styled Components
const LoginPageContainer = styled.div`
  background: #f4f7f9;
  min-height: 100vh;
  color: #333;
  display: flex;
  flex-direction: column;
`;

const LoginContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  padding: 40px 20px;
`;

const LoginCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  padding: 48px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.07);
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
  
  h2 {
    margin: 0 0 8px;
    font-size: 28px;
    color: #101828;
  }
  
  p {
    color: #667085;
    margin: 0;
    font-size: 16px;
  }
`;

const ErrorMessage = styled.div`
  background: #fff1f2;
  border: 1px solid #ffdde0;
  color: #d92d20;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  text-align: center;
`;

const LoginForm = styled.form`
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #344054;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  color: #101828;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4c6fff;
    box-shadow: 0 0 0 3px rgba(76, 111, 255, 0.15);
  }
  
  &::placeholder {
    color: #98a2b3;
  }
`;

const LoginButton = styled(motion.button)`
  width: 100%;
  padding: 12px;
  background: #4c6fff;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  margin-top: 8px;
  transition: background-color 0.2s;

  &:hover {
    background: #3a5ce0;
  }

  &:disabled {
    background: #aeb8d0;
    cursor: not-allowed;
  }
`;

const LoginFooter = styled.div`
  padding-top: 24px;
  text-align: center;
  
  p {
    margin: 0 0 10px;
    color: #667085;
    font-size: 14px;
  }
`;

const CredentialsInfo = styled.div`
  font-size: 14px;
  color: #667085;
  margin-bottom: 16px;
  background: #f4f7f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  text-align: left;

  div {
    margin-bottom: 5px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Note = styled.div`
  font-size: 13px;
  color: #98a2b3;
  font-style: italic;
`;

export default AdminLoginPage;
